import { createQueryKeys } from '@lukemorales/query-key-factory';

import type {
  FetchClientMembershipsArgs,
  FetchMembershipClientsArgs,
  FetchMembershipByIdArgs,
  FetchMembershipStatsArgs,
  FetchMembershipsArgs,
  FetchCheckInsArgs,
} from '../api/types/memberships.types';

import {
  fetchClientMemberships,
  fetchMembershipClients,
  fetchMembershipById,
  fetchMembershipStats,
  fetchMemberships,
  fetchCheckIns,
} from '../api/memberships.api';

export const membershipsQueryKeys = createQueryKeys('memberships', {
  // T10: the cohort filters are part of the identity of the answer — the
  // envelope's `summary` describes the rows the filters selected, so a
  // filtered answer must never be served from the unfiltered entry.
  //
  // The filter segment is APPENDED ONLY when there is one, which is what keeps
  // the eight `invalidateQueries({ exact: false, queryKey: … })` call sites
  // working unchanged: they build the key with no filters, and that key is a
  // PREFIX of every filtered one. The type ids are sorted so the same
  // selection made in a different order is the same cache entry.
  fetchClientMemberships: ({
    clientId,
    from,
    membershipIds,
    organizationId,
    to,
  }: FetchClientMembershipsArgs) => ({
    queryFn: () =>
      fetchClientMemberships({
        clientId,
        from,
        membershipIds,
        organizationId,
        to,
      }),
    queryKey: [
      organizationId,
      clientId,
      'client',
      ...(from || to || membershipIds?.length
        ? [
            {
              from: from ?? null,
              membershipIds: [...(membershipIds ?? [])].sort(),
              to: to ?? null,
            },
          ]
        : []),
    ],
  }),
  fetchMembershipClients: ({
    membershipId,
    organizationId,
  }: FetchMembershipClientsArgs) => ({
    queryFn: () => fetchMembershipClients({ membershipId, organizationId }),
    queryKey: [organizationId, membershipId, 'clients'],
  }),
  fetchMembershipStats: ({ organizationId }: FetchMembershipStatsArgs) => ({
    queryFn: () => fetchMembershipStats({ organizationId }),
    queryKey: [organizationId, 'stats'],
  }),
  fetchMembershipById: ({
    locale,
    membershipId,
    organizationId,
  }: FetchMembershipByIdArgs) => ({
    queryFn: () =>
      fetchMembershipById({ locale, membershipId, organizationId }),
    queryKey: [organizationId, membershipId],
  }),
  fetchMemberships: ({
    locale,
    organizationId,
    status,
  }: FetchMembershipsArgs) => ({
    queryFn: () => fetchMemberships({ locale, organizationId, status }),
    queryKey: [organizationId, status],
  }),
  fetchCheckIns: ({
    clientId,
    organizationId,
    userMembershipId,
  }: FetchCheckInsArgs) => ({
    queryFn: () =>
      fetchCheckIns({ clientId, organizationId, userMembershipId }),
    queryKey: [organizationId, clientId, userMembershipId, 'check-ins'],
  }),
});
